import React, { useEffect, useState } from 'react';
import { Search, Eye, Loader2, BadgeCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import { getAllBrands } from '../../services/adminbrands.api';
import { Brand } from '../../types/adminbrands-model';
import BrandDetailModal from './BrandDetailModal';

const PAGE_SIZE = 10;

const Brands: React.FC = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Detail popup — mirrors the selectedId/open split used in AdminListings.tsx
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const loadBrands = () => {
    setLoading(true);
    getAllBrands()
      .then(setBrands)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadBrands();
  }, []);

  // Reset to page 1 whenever the search term changes
  useEffect(() => {
    setPage(1);
  }, [search]);

  const filtered = brands.filter(
    b =>
      !search ||
      b.companyName.toLowerCase().includes(search.toLowerCase()) ||
      b.contactPerson.toLowerCase().includes(search.toLowerCase()) ||
      b.mobile.toLowerCase().includes(search.toLowerCase()) ||
      (b.gst ?? '').toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginated = filtered.slice(startIndex, startIndex + PAGE_SIZE);

  const stats = [
    { l: 'Total', v: brands.length, c: 'text-amber-600' },
    { l: 'With GST', v: brands.filter(b => !!b.gst).length, c: 'text-emerald-600' },
  ];

  const goToPage = (p: number) => {
    if (p < 1 || p > totalPages) return;
    setPage(p);
  };

  // Builds a compact page-number list with ellipses for large page counts
  const getPageNumbers = (): (number | 'ellipsis')[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages: (number | 'ellipsis')[] = [1];
    if (currentPage > 3) pages.push('ellipsis');
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('ellipsis');
    pages.push(totalPages);
    return pages;
  };

  const openDetail = (id: string) => {
    setSelectedId(id);
    setDetailOpen(true);
  };

  return (
    <div className="min-h-screen bg-stone-50 px-8 py-7">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl font-bold text-stone-800">Brand Management</h1>
          <p className="mt-1 text-[13px] text-stone-500">
            Manage advertiser brands registered on the platform
          </p>
        </div>
        <div className="flex gap-4">
          {stats.map(s => (
            <div key={s.l} className="text-center">
              <div className={`font-serif text-[22px] font-bold ${s.c}`}>{s.v}</div>
              <div className="text-[11px] text-stone-400">{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Search bar */}
      <div className="mb-5 rounded-2xl border border-stone-200 bg-white px-[18px] py-3.5 shadow-sm">
        <div className="relative w-80">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            type="text"
            placeholder="Search by company, contact, mobile or GST..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full rounded-[10px] border border-stone-300 bg-white py-2 pl-9 pr-3 text-[13px] text-stone-700 placeholder:text-stone-400 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] border-collapse">
            <thead>
              <tr className="bg-stone-100">
                {['Sr No.', 'Company', 'Contact Person', 'Mobile', 'GST Number', 'Billing Address', 'Joined', 'Actions'].map(h => (
                  <th
                    key={h}
                    className="whitespace-nowrap px-[18px] py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-400"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="py-12 text-center">
                    <Loader2 size={22} className="mx-auto animate-spin text-amber-600" />
                  </td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-[13px] text-rose-600">
                    {error}
                  </td>
                </tr>
              )}
              {!loading && !error && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-[13px] text-stone-400">
                    No brands found.
                  </td>
                </tr>
              )}
              {!loading &&
                !error &&
                paginated.map((b, idx) => (
                  <tr key={b.id} className="border-b border-stone-100 bg-white last:border-b-0 hover:bg-amber-50/40">
                    <td className="px-[18px] py-3.5 text-[12px] font-semibold text-stone-400">
                      {startIndex + idx + 1}
                    </td>
                    <td className="px-[18px] py-3.5">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-amber-600 text-[11px] font-bold text-white">
                          {b.companyName.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-[13px] font-semibold text-stone-800">{b.companyName}</div>
                          <div className="truncate text-[11px] text-stone-400">{b.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-[18px] py-3.5 text-[12px] text-stone-600">{b.contactPerson}</td>
                    <td className="px-[18px] py-3.5 font-mono text-[12px] text-stone-600">{b.mobile}</td>
                    <td className="px-[18px] py-3.5">
                      {b.gst ? (
                        <span className="inline-flex items-center gap-1 font-mono text-[12px] text-stone-600">
                          <BadgeCheck size={12} className="text-emerald-500" /> {b.gst}
                        </span>
                      ) : (
                        <span className="text-[12px] text-stone-300">—</span>
                      )}
                    </td>
                    <td className="max-w-[220px] truncate px-[18px] py-3.5 text-[12px] text-stone-600">
                      {b.billingAddress}
                    </td>
                    <td className="px-[18px] py-3.5 text-[12px] text-stone-400">{b.edate}</td>
                    <td className="px-[18px] py-3.5">
                      <button
                        onClick={() => openDetail(b.id)}
                        className="flex items-center gap-1 rounded-lg border border-stone-200 bg-stone-100 px-3 py-1.5 text-[11px] font-medium text-stone-600 transition hover:brightness-95"
                      >
                        <Eye size={12} /> View
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && !error && filtered.length > 0 && (
          <div className="flex flex-col gap-3 border-t border-stone-100 px-[18px] py-3.5 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-[12px] text-stone-400">
              Showing <span className="font-medium text-stone-600">{startIndex + 1}</span>–
              <span className="font-medium text-stone-600">
                {Math.min(startIndex + PAGE_SIZE, filtered.length)}
              </span>{' '}
              of <span className="font-medium text-stone-600">{filtered.length}</span> brands
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-stone-200 text-stone-500 transition hover:bg-stone-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronLeft size={14} />
              </button>

              {getPageNumbers().map((p, i) =>
                p === 'ellipsis' ? (
                  <span key={`ellipsis-${i}`} className="px-1.5 text-[12px] text-stone-300">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => goToPage(p)}
                    className={`flex h-8 w-8 items-center justify-center rounded-lg text-[12px] font-medium transition ${
                      p === currentPage
                        ? 'bg-amber-600 text-white shadow-sm shadow-amber-200'
                        : 'text-stone-500 hover:bg-stone-100'
                    }`}
                  >
                    {p}
                  </button>
                )
              )}

              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-stone-200 text-stone-500 transition hover:bg-stone-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail popup — always mounted, visibility controlled by `open` */}
      <BrandDetailModal
        open={detailOpen}
        brandId={selectedId}
        onClose={() => setDetailOpen(false)}
      />
    </div>
  );
};

export default Brands;